import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface NewsletterSettings {
  heading: string;
  subheading: string;
  image: string;
  buttonText: string;
  bgPosition: string;
  bgSize: string;
}

const defaultSettings: NewsletterSettings = {
  heading: 'Receba conteúdo exclusivo para cuidar melhor do seu gato!',
  subheading: 'Cadastre-se e receba conteúdos especiais sobre o universo felino.',
  image: 'https://readdy.ai/api/search-image?query=Adorable%20tabby%20cat%20lying%20down%20on%20soft%20cream%20beige%20blanket%2C%20looking%20up%20with%20curious%20green%20eyes%2C%20warm%20natural%20lighting%2C%20minimalist%20clean%20background%2C%20pet%20portrait%20photography%2C%20high%20detail%2C%20soft%20focus%2C%20cozy%20aesthetic%2C%20horizontal%20banner%20composition%2C%20cat%20positioned%20right%20side&width=1280&height=360&seq=newsletter-cat-banner-01&orientation=landscape',
  buttonText: 'Quero receber',
  bgPosition: 'center',
  bgSize: 'cover',
};

export function useNewsletterSettings() {
  const [settings, setSettings] = useState<NewsletterSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const { data, error: dbError } = await supabase
          .from('newsletter_settings')
          .select('*')
          .eq('id', 1)
          .maybeSingle();
        if (cancelled) return;
        if (dbError) throw dbError;
        if (data) {
          setSettings({
            heading: data.heading || defaultSettings.heading,
            subheading: data.subheading || defaultSettings.subheading,
            image: data.image || defaultSettings.image,
            buttonText: data.button_text || defaultSettings.buttonText,
            bgPosition: data.bg_position || defaultSettings.bgPosition,
            bgSize: data.bg_size || defaultSettings.bgSize,
          });
        }
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Erro ao carregar configurações');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const saveSettings = useCallback(async (newSettings: NewsletterSettings) => {
    try {
      const { error: saveError } = await supabase
        .from('newsletter_settings')
        .upsert({
          id: 1,
          heading: newSettings.heading,
          subheading: newSettings.subheading,
          image: newSettings.image,
          button_text: newSettings.buttonText,
          bg_position: newSettings.bgPosition,
          bg_size: newSettings.bgSize,
          updated_at: new Date().toISOString(),
        });
      if (saveError) throw saveError;
      setSettings(newSettings);
      setError(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
      return false;
    }
  }, []);

  return { settings, loading, error, saveSettings };
}